import { useEffect, useRef } from 'react';

import { useLocation } from '@tanstack/react-router';

/**
 * Scrolls to a hash anchor, polling for asynchronously rendered elements.
 */
export const useScrollToLocation = () => {
    const scrolledRef = useRef(false);
    const { hash } = useLocation();
    const hashRef = useRef(hash);

    useEffect(() => {
        if (!hash) return;

        // Sportsbook uses hash routing (e.g., #/overview), which should not trigger anchor scroll.
        if (hash.includes('/')) return;

        if (hashRef.current !== hash) {
            hashRef.current = hash;
            scrolledRef.current = false;
        }

        if (scrolledRef.current) return;

        const id = hash.replace('#', '');
        const timeoutDuration = 3000;
        const startTime = performance.now();
        let rafId = 0;

        const attemptScroll = () => {
            const element = document.getElementById(id);

            if (element) {
                // CSS scroll-margin-top handles the offset for sticky header
                element.scrollIntoView({ behavior: 'smooth' });
                scrolledRef.current = true;
            } else if (performance.now() - startTime < timeoutDuration) {
                rafId = requestAnimationFrame(attemptScroll);
            }
        };

        rafId = requestAnimationFrame(attemptScroll);

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [hash]);
};
